import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, BookOpen, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox } from "@/components/common";
import { Markdown } from "@/components/Markdown";
import { useApp } from "@/lib/store";

interface Doc {
  slug: string;
  title: string;
  subtitle: string;
  content: string;
}

interface Template {
  slug: string;
  command: string;
  title: string;
  onboarding_slug: string;
  status: string;
}

export function OnboardingDoc() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const ws = useApp((s) => s.workspaceId);
  const qc = useQueryClient();
  const [installed, setInstalled] = useState(false);

  const doc = useQuery<Doc>({
    queryKey: ["onboarding", slug],
    queryFn: () => rawFetch(`/packs/marketplace/onboarding/${slug}/`),
  });

  const templates = useQuery<{ results: Template[] }>({
    queryKey: ["marketplace-templates"],
    queryFn: () => rawFetch("/packs/marketplace/templates/"),
  });
  const matched = templates.data?.results.find((t) => t.onboarding_slug === slug);

  const install = useMutation({
    mutationFn: async () => {
      if (!matched) throw new Error("No installable template for this doc.");
      return await rawFetch(`/packs/marketplace/templates/${matched.slug}/install/`, {
        method: "POST",
        body: JSON.stringify({ workspace_id: ws }),
      });
    },
    onSuccess: () => {
      setInstalled(true);
      qc.invalidateQueries({ queryKey: ["pack-installs"] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
    },
  });

  if (doc.isLoading) {
    return (
      <div className="card text-muted text-sm">Loading onboarding doc…</div>
    );
  }
  if (doc.isError) {
    return <ErrorBox error={doc.error} />;
  }
  if (!doc.data) return null;

  return (
    <>
      <PageHeader
        title={doc.data.title}
        subtitle={doc.data.subtitle}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/marketplace" className="btn inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Back to marketplace
            </Link>
            {matched && (
              <button
                className="btn-primary inline-flex items-center gap-1"
                disabled={!ws || install.isPending}
                onClick={() => install.mutate()}
                title={ws ? `Install ${matched.title} into the current workspace`
                          : "Pick a workspace first"}
              >
                {install.isPending ? <Loader2 size={14} className="animate-spin" /> :
                 installed ? <CheckCircle2 size={14} /> : <Sparkles size={14} />}
                {install.isPending ? "Installing…" :
                 installed ? "Installed" : `Install ${matched.title}`}
              </button>
            )}
          </div>
        }
      />

      {install.isError && (
        <div className="mb-3">
          <ErrorBox error={install.error} />
        </div>
      )}

      <article className="card prose-onb">
        <Markdown source={doc.data.content} />
      </article>

      <div className="mt-4 flex items-center gap-2 text-xs text-muted">
        <BookOpen size={12} />
        Source: <code className="font-mono">docs/partners/{slug}.md</code>
      </div>
    </>
  );
}

